export default function TermsOfService() {
  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-md p-8 md:p-12">
        <h1 className="text-4xl font-bold text-gray-900 mb-2">Terms of Service</h1>
        <p className="text-sm text-gray-600 mb-8">Last Updated: October 28, 2025</p>

        <div className="prose prose-lg max-w-none">
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Agreement to Terms</h2>
            <p className="text-gray-700 mb-4">
              By accessing or using Hello.Reviews ("Service"), you agree to be bound by these Terms of Service ("Terms").
              If you disagree with any part of these terms, you may not access the Service.
            </p>
            <p className="text-gray-700">
              <strong>Service Provider:</strong> Hello.Reviews
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Description of Service</h2>
            <p className="text-gray-700 mb-4">Hello.Reviews provides review management services, including:</p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li><strong>Done-For-You Service</strong> - We manage review collection and moderation on your behalf</li>
              <li><strong>DIY Software</strong> - Self-service platform for review management (when available)</li>
              <li><strong>Setup & Consulting</strong> - One-time setup and strategy consultation</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. Service Tiers & Pricing</h2>

            <div className="mb-4">
              <h3 className="text-xl font-semibold text-gray-800 mb-2">Done-For-You Service ($299/month)</h3>
              <ul className="list-disc pl-6 text-gray-700 space-y-1">
                <li>Review collection from your customers</li>
                <li>Review moderation and routing (public/private)</li>
                <li>Content activation (social posts, testimonials)</li>
                <li>Monthly reporting</li>
              </ul>
            </div>

            <div className="mb-4">
              <h3 className="text-xl font-semibold text-gray-800 mb-2">DIY Software ($49/month - when available)</h3>
              <ul className="list-disc pl-6 text-gray-700 space-y-1">
                <li>Self-service platform access</li>
                <li>Review request tools</li>
                <li>Analytics dashboard</li>
                <li>Email support</li>
              </ul>
            </div>

            <div className="mb-4">
              <h3 className="text-xl font-semibold text-gray-800 mb-2">Payment Terms</h3>
              <ul className="list-disc pl-6 text-gray-700 space-y-1">
                <li>Monthly subscriptions billed on the first of each month</li>
                <li>One-time fees due upon service completion</li>
                <li>All fees are in USD</li>
                <li>Refunds available within 30 days of first payment if guarantee not met</li>
              </ul>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Our Guarantee</h2>
            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-4">
              <p className="text-gray-800 font-semibold">Done-For-You Service Guarantee:</p>
              <p className="text-gray-700">We guarantee 10 new reviews in your first 30 days, or you don't pay for that month.</p>
            </div>
            <p className="text-gray-700 mb-2"><strong>Conditions:</strong></p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>You must provide a list of at least 20 recent customers</li>
              <li>You must approve review request templates within 48 hours</li>
              <li>You must respond to our communication within 2 business days</li>
              <li>Customers must have had a positive experience (we cannot force positive reviews)</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Your Responsibilities</h2>
            <p className="text-gray-700 mb-2">You agree to:</p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>Provide accurate customer contact information</li>
              <li>Approve review request messages before we send them</li>
              <li>Only request reviews from customers who had a positive experience</li>
              <li>Comply with all review platform policies (Google, Yelp, etc.)</li>
              <li>Not request fake, paid, or incentivized reviews</li>
              <li>Pay invoices on time</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. What We Will NOT Do</h2>
            <div className="bg-red-50 border-l-4 border-red-500 p-4">
              <p className="text-gray-700 mb-2">We will not:</p>
              <ul className="list-disc pl-6 text-gray-700 space-y-1">
                <li>Write fake reviews</li>
                <li>Pay for reviews</li>
                <li>Incentivize customers to leave positive reviews</li>
                <li>Violate any review platform's terms of service</li>
                <li>Share your customer data with third parties (except as required to deliver services)</li>
              </ul>
            </div>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Limitation of Liability</h2>
            <p className="text-gray-700 mb-4">To the maximum extent permitted by law:</p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>Our liability is limited to fees paid in the last 12 months</li>
              <li>We are not liable for indirect, consequential, or punitive damages</li>
              <li>We are not liable for customer reviews or platform removals</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Contact</h2>
            <p className="text-gray-700">Questions about these Terms?</p>
            <p className="text-gray-700 mt-2">
              <strong>Website:</strong> <a href="https://hello.reviews" className="text-blue-600 hover:underline">https://hello.reviews</a>
            </p>
            <p className="text-gray-700">
              <strong>Response Time:</strong> Within 2 business days
            </p>
          </section>

          <div className="border-t border-gray-200 pt-8 mt-8">
            <p className="text-sm text-gray-600 italic">
              This is a legal document. We recommend having an attorney review it for your specific situation.
            </p>
            <p className="text-sm text-gray-600 mt-2">
              <a href="/#home" className="text-blue-600 hover:underline">← Back to Home</a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
